/**
 * Synapse Upload Hook
 *
 * Thin wrapper around Synapse upload atoms for use in React components.
 * Handles configuration, side effects, and provides a clean API.
 *
 * This replaces the state-heavy useSynapseAudioUpload implementation
 * with atom-based state management.
 */
import { useCallback, useEffect, useRef } from 'react';

import { useAtomValue, useSetAtom } from 'jotai';

import { createDebugLogger } from '@videa/utils';

import {
  type SynapseTrackFn,
  configureSynapseAnalytics,
} from '../analytics/synapseAnalytics';
import { recordingSessionIdAtom } from '../atoms/recording';
import {
  type SynapseUploadError,
  ambientSessionIdAtom,
  clearAmbientSessionAtom,
  completeAmbientSessionAtom,
  configureSynapseUploadAtom,
  isAmbientSessionActiveAtom,
  isSynapseConfiguredAtom,
  pendingSessionCreationAtom,
  queueChunkUploadAtom,
  registeredSessionPairAtom,
  retrySessionCreationAtom,
  startAmbientSessionAtom,
  synapseUploadConfigAtom,
  synapseUploadErrorAtom,
  synapseUploadLoadingAtom,
  uploadWorkerAvailableAtom,
} from '../atoms/synapseUpload';
import type { CreateSessionParams } from '../upload/ambientClient';
import {
  cacheTokenInServiceWorker,
  registerUploadSession,
  triggerQueueProcessing,
} from '../upload/backgroundSync';

const debug = createDebugLogger('useSynapseUpload');

// ============================================================================
// Types
// ============================================================================

export interface UseSynapseUploadConfig {
  /** Synapse Ambient API URL */
  synapseApiUrl: string;
  /** Current organization ID */
  organizationId: string;
  /** Current practice ID */
  practiceId: string;
  /** Token provider function (returns Okta access token) */
  getToken: () => Promise<string>;
  /** Whether Synapse upload is enabled (feature flag) */
  enabled?: boolean;
  /** Analytics tracking function (e.g., from useMixpanel) */
  trackEvent?: SynapseTrackFn;
}

export interface StartSessionOptions {
  /** Session parameters for Weaver */
  sessionParams: CreateSessionParams;
  /** OPFS session ID (if available) */
  opfsSessionId?: string;
  /** Recovered session ID (skip creating new) */
  recoveredAmbientSessionId?: string;
}

export interface UseSynapseUploadReturn {
  /** Whether Synapse upload is properly configured */
  isConfigured: boolean;
  /** Whether an ambient session is active */
  isSessionActive: boolean;
  /** Current ambient session ID */
  ambientSessionId: string | null;
  /** Whether an operation is in progress */
  isLoading: boolean;
  /** Current error state */
  error: SynapseUploadError | null;
  /** Start an ambient session */
  startSession: (options: StartSessionOptions) => Promise<string | null>;
  /** Queue a chunk for upload */
  queueChunk: (chunkIndex: number, opfsSessionId: string) => Promise<void>;
  /** Complete the session and get nucleus S3 key */
  completeSession: (localTranscript?: string) => Promise<{
    nucleusS3Key: string;
    durationSeconds: number;
    elapsedTime: number;
  } | null>;
  /** Clear session state */
  clearSession: () => void;
}

// ============================================================================
// Hook
// ============================================================================

export function useSynapseUpload(
  config: UseSynapseUploadConfig,
): UseSynapseUploadReturn {
  const {
    synapseApiUrl,
    organizationId,
    practiceId,
    getToken,
    enabled = true,
    trackEvent,
  } = config;

  // Configure analytics tracking on mount
  useEffect(() => {
    if (trackEvent) {
      configureSynapseAnalytics(trackEvent);
    }
    return () => {
      // Clear analytics on unmount
      configureSynapseAnalytics(null);
    };
  }, [trackEvent]);

  // Atoms
  const configure = useSetAtom(configureSynapseUploadAtom);
  const startAmbientSession = useSetAtom(startAmbientSessionAtom);
  const queueChunkUpload = useSetAtom(queueChunkUploadAtom);
  const completeAmbientSession = useSetAtom(completeAmbientSessionAtom);
  const clearAmbientSession = useSetAtom(clearAmbientSessionAtom);
  const retrySessionCreation = useSetAtom(retrySessionCreationAtom);
  const isConfigured = useAtomValue(isSynapseConfiguredAtom);
  const isSessionActive = useAtomValue(isAmbientSessionActiveAtom);
  const ambientSessionId = useAtomValue(ambientSessionIdAtom);
  const isLoading = useAtomValue(synapseUploadLoadingAtom);
  const error = useAtomValue(synapseUploadErrorAtom);
  const pendingCreation = useAtomValue(pendingSessionCreationAtom);
  const opfsSessionId = useAtomValue(recordingSessionIdAtom);
  const registeredPair = useAtomValue(registeredSessionPairAtom);
  const uploadConfig = useAtomValue(synapseUploadConfigAtom);
  const setRegisteredPair = useSetAtom(registeredSessionPairAtom);
  const swAvailable = useAtomValue(uploadWorkerAvailableAtom);

  // Track last session params for retry
  const lastSessionParamsRef = useRef<CreateSessionParams | null>(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      lastSessionParamsRef.current = null;
    };
  }, []);

  // Listen for TOKEN_REFRESH_NEEDED from service worker.
  // When the SW gets a 401 during chunk upload, it asks the main thread
  // for a fresh token so background-sync retries succeed.
  useEffect(() => {
    if (!enabled || !('serviceWorker' in navigator)) return;

    const handler = async (event: MessageEvent) => {
      if (event.data?.type === 'TOKEN_REFRESH_NEEDED') {
        debug.log('SW requested token refresh');
        try {
          const freshToken = await getToken();
          await cacheTokenInServiceWorker(freshToken);
          await triggerQueueProcessing();
          debug.log('Token refreshed for SW, triggered queue processing');
        } catch (err) {
          debug.error('Failed to refresh token for SW:', err);
        }
      }
    };

    navigator.serviceWorker.addEventListener('message', handler);
    return () => {
      navigator.serviceWorker.removeEventListener('message', handler);
    };
  }, [enabled, getToken]);

  // Configure on mount/change
  useEffect(() => {
    if (!enabled || !synapseApiUrl) {
      debug.log('Synapse upload disabled or not configured');
      return;
    }

    debug.log('Configuring Synapse upload');
    configure({
      config: {
        synapseApiUrl,
        organizationId,
        practiceId,
      },
      tokenProvider: getToken,
    });
  }, [enabled, synapseApiUrl, organizationId, practiceId, getToken, configure]);

  // Late OPFS registration effect
  // Handles race condition where startAmbientSession runs before OPFS session exists.
  // Skipped when the upload SW is unavailable (swAvailable === false) to avoid
  // a 2s timeout on registerUploadSession that would go to the wrong SW.
  useEffect(() => {
    if (
      !opfsSessionId ||
      !ambientSessionId ||
      !uploadConfig ||
      swAvailable === false
    ) {
      return;
    }

    // Check if already registered
    const expectedPair = `${opfsSessionId}:${ambientSessionId}`;
    if (registeredPair === expectedPair) {
      return;
    }

    // Register with service worker now that both IDs are available
    debug.log('Late registration: OPFS session now available', {
      opfsSessionId,
      ambientSessionId,
    });

    registerUploadSession(
      opfsSessionId,
      ambientSessionId,
      uploadConfig.organizationId,
      uploadConfig.practiceId,
      uploadConfig.synapseApiUrl,
    )
      .then(() => {
        setRegisteredPair(expectedPair);
        return triggerQueueProcessing();
      })
      .catch((err) => {
        debug.error('Failed late session registration:', err);
      });
  }, [
    opfsSessionId,
    ambientSessionId,
    uploadConfig,
    registeredPair,
    setRegisteredPair,
    swAvailable,
  ]);

  // Background retry effect
  useEffect(() => {
    if (
      !pendingCreation ||
      !opfsSessionId ||
      ambientSessionId ||
      !lastSessionParamsRef.current
    ) {
      return;
    }

    const RETRY_INTERVAL_MS = 5000;

    const timeoutId = setTimeout(() => {
      if (lastSessionParamsRef.current) {
        retrySessionCreation({ sessionParams: lastSessionParamsRef.current });
      }
    }, RETRY_INTERVAL_MS);

    return () => clearTimeout(timeoutId);
  }, [pendingCreation, opfsSessionId, ambientSessionId, retrySessionCreation]);

  // Start session
  const startSession = useCallback(
    async (options: StartSessionOptions): Promise<string | null> => {
      if (!enabled) {
        debug.warn('Synapse upload not enabled');
        return null;
      }

      // Store params for potential retry
      lastSessionParamsRef.current = options.sessionParams;

      return startAmbientSession({
        sessionParams: options.sessionParams,
        opfsSessionId: options.opfsSessionId,
        recoveredAmbientSessionId: options.recoveredAmbientSessionId,
      });
    },
    [enabled, startAmbientSession],
  );

  // Queue chunk
  const queueChunk = useCallback(
    async (chunkIndex: number, sessionId: string): Promise<void> => {
      await queueChunkUpload({ chunkIndex, opfsSessionId: sessionId });
    },
    [queueChunkUpload],
  );

  // Complete session
  const completeSession = useCallback(
    async (
      localTranscript?: string,
    ): Promise<{
      nucleusS3Key: string;
      durationSeconds: number;
      elapsedTime: number;
    } | null> => {
      const result = await completeAmbientSession({ localTranscript });

      if (!result) {
        return null;
      }

      // Calculate elapsed time at completion, not at callback creation
      const completionElapsedTime =
        result.startTime > 0 ? Date.now() - result.startTime : 0;

      return {
        nucleusS3Key: result.nucleusS3Key,
        durationSeconds: result.durationSeconds,
        elapsedTime: completionElapsedTime,
      };
    },
    [completeAmbientSession],
  );

  // Clear session
  const clearSession = useCallback(() => {
    lastSessionParamsRef.current = null;
    clearAmbientSession();
  }, [clearAmbientSession]);

  return {
    isConfigured: enabled && isConfigured,
    isSessionActive,
    ambientSessionId,
    isLoading,
    error,
    startSession,
    queueChunk,
    completeSession,
    clearSession,
  };
}
